import * as _ from 'underscore';
import { Helpers } from '../helpers/Helpers';
import { Module } from '../Module';
import { IOAuthCredentialTypeInfo } from '../ServerApi/types/IOAuthCredential';
import { HelpService } from './HelpService';
import { IOAuthCredentialsService } from './OAuthCredentialsService';
import { ServerService } from './ServerService';
import { SessionService } from './SessionService';
import { IOpenWindowSpecs, WindowLocationService } from './WindowLocationService';

export interface IAddOAuthResponseListenerWindow extends ng.IWindowService {
  tableauPopupComplete(response?: string): void;
}

// NOTE: Keep this in sync with the code in embeddedCommon.pug
export class AddOAuthCredentialAction {

  public static OAuthPath = '/auth/request_oauth';
  public static WindowName = 'TableauAuth';
  private static AddGenericAuthCredentialsPath = '/auth/add_generic_auth_credentials';
  private static InitMsgName = 'init';
  private static AuthPhaseName = 'auth';

  public static $inject = [
    '$window',
    '$q',
    'WindowLocationService',
    '$http',
    'OAuthCredentialsService',
    'ServerService',
    'HelpService',
    'SessionService'];

  private openWindow: Window;
  private deferred: ng.IDeferred<string>;
  // Based on different connectionType, we will use try auth editing in different ways
  // especially for first-class WDC-based connections
  private dataConnectionType: string;
  private instanceUrl: string;

  constructor(
    private $window: IAddOAuthResponseListenerWindow,
    private $q: ng.IQService,
    private WindowLocationService: WindowLocationService,
    private $http: ng.IHttpService,
    private oAuthCredentialsService: IOAuthCredentialsService,
    private serverService: ServerService,
    private helpService: HelpService,
    private sessionService: SessionService) {
    this.deferred = $q.defer<string>(); // Noop deferred to remove null checking logic

    // SET UP GLOBAL LISTENERS

    $window.tableauPopupComplete = (response: string) => this.handleResponse(response);

    $($window).on('message', (jqEvent: JQuery.Event) => {
      // casted to <any> because JQueryEvent originalEvent is expected to be a PopStateEvent which is not compatible with MessageEvent
      const originalEvent: MessageEvent = <any>jqEvent.originalEvent;

      if(originalEvent.source !== this.openWindow) return;

      const response: string = originalEvent.data;

      if(!_.isString(response)) return;

      this.handleResponse(response);
    });

    // The user is navigating away from this page so the popup is no longer needed, let's try to remove it for them.
    $($window).on('unload', () => this.removeOpenWindow());
  }

  private handleResponse(response: string = '') {
    if (this.oAuthCredentialsService.supportsGenericAuth(this.dataConnectionType)) {
      this.handleGenericAuthResponse(response);
    } else {
      this.handleCustomOAuthResponse(response);
    }
  }

  private handleGenericAuthResponse(response: string) {
    const responseObj = JSON.parse(response);
    if (responseObj.msgName === 'loaded') {
      // When the home page for WDC is loaded, post a message to initialize it
      this.sendInitAuthMessage();
    } else if (responseObj.msgName === 'submit') {
      this.saveGenericAuthCredentials(responseObj);
    }
  }

  private saveGenericAuthCredentials(responseObj: any) {
    const formData = new FormData();
    formData.append('data_class', this.dataConnectionType);
    formData.append('username', responseObj.props.username);
    formData.append('password', responseObj.props.password);
    if (this.instanceUrl) {
      formData.append('instance_url', this.instanceUrl);
    }

    if (responseObj.props.usernameAlias) {
      formData.append('username_alias', responseObj.props.usernameAlias);
    }

    const addCredentialPromise = this.$http({
      method: 'POST',
      // Do not serialize into JSON (which is Angular's default)
      transformRequest: (data: any) => data,
      // Allow browser to choose Content-Type to get boundary needed
      // see: https://groups.google.com/d/msg/angular/MBf8qvBpuVE/krmd75giKJUJ
      headers: {
        'Content-Type': undefined,
        'X-XSRF-TOKEN': this.sessionService.getSessionId()
      },
      url: AddOAuthCredentialAction.AddGenericAuthCredentialsPath,
      data: formData
    });

    addCredentialPromise.then((result: any) => {
      const data = JSON.stringify(result.data);
      this.handleCustomOAuthResponse(data);
    });
  }

  private sendInitAuthMessage() {
    if (!this.openWindow) {
      return;
    }

    const version = this.serverService.sessionServerVersion();

    // For first-class WDC, load WDC url and send an init message to the window
    const emptyMessage = {
      'msgRecipient': 'webdataconnector',
      'msgName': AddOAuthCredentialAction.InitMsgName,
      'msgData': { 'phase': AddOAuthCredentialAction.AuthPhaseName },
      'props': {
        'authPurpose': 'enduring',
        'connectionName': '',
        'connectionData': '',
        'password': '',
        'username': '',
        'incrementalExtractColumn': '',
        'versionNumber': '2.0', // This is for WDC shim libary 2.0.* versions
        'locale': this.helpService.helpUrlLanguage(),
        'platformBuildNumber': version.build,
        'platformEdition': 'server',
        'platformOS': version.platform,
        'platformVersion': this.helpService.externalVersion()
      }
    };

    this.openWindow.postMessage(JSON.stringify(emptyMessage), '*');
  }

  private handleCustomOAuthResponse(response: string) {
    const errorCodeMatch = response.match(/errorCode=([^(&|")]*)/);
    if(errorCodeMatch) {
      const errorCode = parseInt(errorCodeMatch[1], 10) || 0;
      this.notifyError(errorCode);
      return;
    }

    const newCredentialIdMatch = response.match(/id=([^(&|")]*)/);
    if(newCredentialIdMatch) {
      this.notifySuccess(newCredentialIdMatch[1]);
      return;
    }

    this.notifySuccess('');
  }

  private removeOpenWindow() {
    try {
      this.openWindow.close();
    } catch (e) {
    } // Closing the window is best effort so there's nothing to do if it fails

    this.openWindow = null;
  }

  private notifySuccess(credentialId?: string) {
    this.deferred.resolve(credentialId);
    this.removeOpenWindow();
  }

  private notifyError(errorCode?: number) {
    this.deferred.reject(errorCode);
    this.removeOpenWindow();
  }

  private getPopupSpecs(): IOpenWindowSpecs {
    const popupOptions: IOpenWindowSpecs = {
      width: 800,
      height: 600,
      toolbar: 1,
      resizable: 1
    };

    // IE8 polyfills. Note that outer* ones are not quite right for IE8, but good enough. B147312
    const screenLeft = this.$window.screenLeft || this.$window.screenX;
    const outerWidth = this.$window.outerWidth || this.$window.innerWidth;
    const screenTop = this.$window.screenTop || this.$window.screenY;
    const outerHeight = this.$window.outerHeight || this.$window.innerHeight;


    // Open horizontally centered on current window
    popupOptions.left = screenLeft + (outerWidth - popupOptions.width) / 2;

    // Open a small amount from the top
    popupOptions.top = screenTop + (outerHeight - popupOptions.height) / 8;

    return popupOptions;
  }

  // Public methods

  public href(typeInfo: IOAuthCredentialTypeInfo, instanceurl?: string): string {
    // Redirect to server which will take care of redirecting to the appropriate place based on data_class
    let serialized;
    if (instanceurl) {
      serialized = Helpers.serialize({ 'data_class':typeInfo.type, 'instanceURL' : instanceurl });
    } else {
      serialized = Helpers.serialize({ 'data_class':typeInfo.type });
    }
    return AddOAuthCredentialAction.OAuthPath + '?' + serialized + '&' +
      Helpers.serialize({ 'secure' : (window.location.protocol.toLowerCase() === 'https:') });
  }

  public attemptAuth(connectionType: string, instanceurl: string = null): ng.IPromise<string> {
    this.cancelPendingAuthAttempt();

    const typeInfo = this.oAuthCredentialsService.getTypeInfo(connectionType);

    this.deferred = this.$q.defer<string>();
    this.instanceUrl = instanceurl;
    this.dataConnectionType = typeInfo.type;
    this.openWindow = this.WindowLocationService.open(this.href(typeInfo, instanceurl), AddOAuthCredentialAction.WindowName, this.getPopupSpecs());

    try{
      this.openWindow.focus(); // Bring the newly opened window to the front
    } catch(e) {}

    return this.deferred.promise;
  }

  public cancelPendingAuthAttempt() {
    return this.notifyError();
  }
}

Module.instance().service('AddOAuthCredentialAction', AddOAuthCredentialAction);
